import { useState } from "react";
import { FileText, X, Briefcase } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { STRENGTHS_PROFILE, THEME_DESCRIPTIONS, BENEFITS } from "@/data/portfolio";
import Benefit from "@/components/site/Benefit";
import { track } from "@/lib/analytics";
import { T, useLang } from "@/lib/i18n";

const { domains, top5, all34, completed, reportUrl } = STRENGTHS_PROFILE;
const domainOf = (name) => Object.entries(domains).find(([, d]) => d.themes.includes(name))?.[0];

function DomainPill({ name, className = "" }) {
  const d = domains[domainOf(name)];
  return (
    <span className={`px-2 py-0.5 rounded-full text-[8px] font-mono-label text-white whitespace-nowrap ${className}`} style={{ background: d.color }}>
      {d.label}
    </span>
  );
}

export default function Personality() {
  const { t } = useLang();
  const [theme, setTheme] = useState(null);
  const top10 = all34.slice(0, 10);
  const openTheme = (name) => {
    setTheme(name);
    track("strength_open", { theme: name });
  };
  const rank = theme ? all34.indexOf(theme) + 1 : 0;
  const sig = theme ? top5.find((x) => x.name === theme) : null;

  return (
    <section id="personality" data-testid="personality-section" className="border-t border-[#e5e0d8] section-pad bg-[#faf8f5]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-mono-label text-[11px] text-[#9a7218]">{t("03 · The insightful influencer")}</div>
            <p className="mt-3 text-sm text-[#55625b] leading-relaxed">
              {t("Gallup CliftonStrengths 34, completed November 2024. My five signature themes, and what they look like at work.")}
            </p>
          </div>
          <a
            href={reportUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="strengths-report-link"
            onClick={() => track("external_link", { label: "cliftonstrengths-report" })}
            className="inline-flex items-center gap-1.5 font-mono-label text-[11px] text-[#14352a] link-underline"
          >
            <FileText className="h-3.5 w-3.5" /> {t("Full Gallup report (PDF)")}
          </a>
        </div>

        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {top5.map((s, i) => (
            <button
              key={s.name}
              type="button"
              data-testid={`strength-top-${i}`}
              onClick={() => openTheme(s.name)}
              className="text-left flex flex-col p-5 bg-white border border-[#e5e0d8] hover:border-[#9a7218] tilt-hover"
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-3xl text-[#9a7218] leading-none">0{i + 1}</span>
                <DomainPill name={s.name} />
              </div>
              <h3 className="mt-3 font-display text-2xl text-[#14352a] leading-none">{s.name}</h3>
              <p className="mt-2 text-sm text-[#55625b] leading-relaxed">{t(s.gallup)}</p>
              <p className="mt-3 pt-3 border-t border-[#e5e0d8] text-sm text-[#14352a] leading-relaxed">
                <span className="font-mono-label text-[8px] text-[#9a7218] block mb-1">{t("At work")}</span>
                {t(s.atWork)}
              </p>
              <p data-testid={`strength-brief-${i}`} className="mt-3 pt-3 border-t border-[#e5e0d8] text-[13px] text-[#55625b] leading-relaxed italic">
                <span className="font-mono-label text-[8px] text-[#9a7218] not-italic inline-flex items-center gap-1 mb-1"><Briefcase className="h-3 w-3" /> {t("In the brief")}</span>
                <br />&ldquo;{t(s.brief)}&rdquo;
              </p>
            </button>
          ))}
        </div>

        <div data-testid="personality-differentiator" className="mt-8 p-6 sm:p-8 bg-[#14352a] text-[#faf8f5] border-l-4 border-[#d9a92e] grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <div className="font-mono-label text-[9px] text-[#d9a92e]">{t("Researcher and business strategist, in one profile")}</div>
          </div>
          <div className="lg:col-span-8 space-y-4 text-base leading-relaxed text-[#faf8f5]/85">
            <p>{t("Most researchers stop at the insight. Most strategists start without one. My profile closes that gap: Woo and Communication get me into the room with customers and executives; Strategic and Input turn what I hear into patterns and options; Significance means I take the recommendation all the way to the decision, and own it.")}</p>
            <p>{t("For a company building experience-first products on a data-driven, iterative foundation, that is the profile you want owning customer insights: someone who can run global discovery on Monday, present the commercial case on Friday, and adapt when the plan changes on Wednesday.")}</p>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-12 p-5 bg-white border border-[#e5e0d8]" data-testid="strengths-domains">
            <div className="font-mono-label text-[10px] text-[#55625b]">{t("Top 10 themes by domain")}</div>
            <ul className="mt-4 space-y-3">
              {Object.entries(domains).map(([key, d]) => {
                const n = top10.filter((x) => d.themes.includes(x)).length;
                return (
                  <li key={key} data-testid={`domain-bar-${key}`}>
                    <div className="flex items-baseline justify-between text-sm text-[#14352a]">
                      <span>{d.label}</span>
                      <span className="font-mono-label text-[9px] text-[#86928a]">{n} / 10</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-[#f2ece4] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${n * 10}%`, background: d.color }} />
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-4 text-xs text-[#55625b] leading-relaxed">{t(STRENGTHS_PROFILE.summary)}</p>
          </div>
        </div>

        <Benefit text={BENEFITS.personality} testid="benefit-personality" />
      </div>

      <Dialog open={!!theme} onOpenChange={(o) => !o && setTheme(null)}>
        <DialogContent data-testid="strength-dialog" className="max-w-md p-0 overflow-hidden rounded-2xl border-[#e5e0d8] bg-[#faf8f5] [&>button]:hidden">
          {theme && (
            <div>
              <div className="p-6 bg-[#14352a] text-[#faf8f5]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 font-mono-label text-[9px] text-[#d9a92e]">
                      #{rank} / 34 <DomainPill name={theme} />
                    </div>
                    <DialogTitle className="mt-2 font-display text-3xl leading-tight font-medium">{theme}</DialogTitle>
                    <DialogDescription data-testid="strength-dialog-desc" className="mt-2 text-sm text-[#faf8f5]/80 leading-relaxed">
                      {t(THEME_DESCRIPTIONS[theme])}
                    </DialogDescription>
                  </div>
                  <button data-testid="strength-dialog-close" onClick={() => setTheme(null)} className="h-8 w-8 shrink-0 grid place-items-center rounded-full hover:bg-white/10" aria-label="Close">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {sig && (
                <div className="p-6 space-y-3 text-sm text-[#14352a] leading-relaxed">
                  <p><span className="font-mono-label text-[8px] text-[#9a7218] block mb-1">{t("At work")}</span>{t(sig.atWork)}</p>
                  <p className="italic"><span className="font-mono-label text-[8px] text-[#9a7218] block mb-1 not-italic">{t("In the brief")}</span>&ldquo;{t(sig.brief)}&rdquo;</p>
                </div>
              )}
              <div className="px-6 pb-5 font-mono-label text-[8px] text-[#86928a]">Gallup® CliftonStrengths® theme description · verbatim</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
